import { FC, useCallback, useEffect, useMemo } from "react";
import { t } from "i18next";
import { isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "reactstrap";

import { Skeleton } from "@components/shared/Skeleton/Skeleton";
import { DECIMAL_PLACES } from "@constants/numbers";
import { LOCK_STATUS } from "@constants/unitDetails";
import { useAppMode } from "@contexts/app-mode";
import { useLocationContext } from "@contexts/location/LocationContext";
import { DataTypes } from "@enums/shared.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { usePricing } from "@hooks/usePricing/usePricing";
import { ResourceDurationPropType } from "@models/components/booking.model";
import { applyLocalization } from "@services/common.service";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";

const ResourceDuration: FC<ResourceDurationPropType> = ({
  isBookingDisable,
  priceLoader,
  resource,
  selectSlotFromDrawer,
  showResourceTitle = true,
}) => {
  const {
    backToBackBookingEnabled,
    bookingDataChanged,
    bookingEditData,
    editBookingWarning,
    isEditBooking,
    resourcePrices,
    selectedSlot,
    selectedTime,
  } = useSelector((State: RootState) => State.booking);
  const { isKioskMode } = useAppMode();
  const {
    disableResource,
    getAvailableDurations,
    isSlotAvailable,
    resourceActiveClass,
    selectSlot,
  } = useBooking();
  const dispatch = useDispatch();
  const { activeLocation } = useLocationContext();
  const { getPriceByResourceSlot } = usePricing();

  const isFinanceConfigured = activeLocation?.financeConfigured || false;

  const { durations, warningMessage } = useMemo(
    () => getAvailableDurations(resource),
    [resource, selectedTime]
  );
  const hasWarningMessage =
    warningMessage?.quote?.length > 0 ||
    warningMessage?.advanceBookingMsg !== "";

  const preSelectSlotIfEmpty = useCallback(() => {
    if (
      !selectedTime ||
      durations?.length === 0 ||
      hasWarningMessage ||
      !isEmpty(selectedSlot) ||
      !selectSlotFromDrawer
    ) {
      return;
    }

    selectSlotFromDrawer(durations[0], resource.id);
  }, [
    selectedTime,
    durations,
    hasWarningMessage,
    selectedSlot,
    selectSlotFromDrawer,
    resource?.id,
  ]);

  useEffect(() => {
    preSelectSlotIfEmpty();
  }, [preSelectSlotIfEmpty]);

  const handleSlotClick = selectSlotFromDrawer || selectSlot;
  const setHideDurationWarning: () => void = () => {
    dispatch(
      bookingSlice.actions.setEditBookingWarning({
        duration: false,
        resource: false,
      })
    );
  };
  useEffect(() => {
    if (!isEditBooking || backToBackBookingEnabled) return;

    const isInvalid =
      bookingEditData &&
      resource.id === bookingEditData?.resourceId &&
      !isSlotAvailable(durations, bookingEditData.timeSlot);

    if (!bookingDataChanged && isInvalid) {
      dispatch(
        bookingSlice.actions.setEditBookingWarning({
          duration: !!isInvalid,
        })
      );
    }
  }, [bookingEditData, backToBackBookingEnabled]);

  useEffect(() => {
    if (backToBackBookingEnabled) {
      if (!isEditBooking) return;

      const isInvalid =
        bookingEditData &&
        resource.id === bookingEditData?.resourceId &&
        !isSlotAvailable(durations, bookingEditData.timeSlot);

      if (!bookingDataChanged) {
        dispatch(
          bookingSlice.actions.setEditBookingWarning({
            duration: !!isInvalid,
          })
        );
      }
    }
  }, [bookingEditData, durations, backToBackBookingEnabled]);

  const isResourceLocked = resource?.lockStatus === LOCK_STATUS.CLOSE;
  return (
    <>
      <div className="fg-unit-det">
        <div
          className={`fg-unit-det-wrap ${disableResource(isBookingDisable || (!isKioskMode && isResourceLocked), durations)}`}
          data-testid="units-block-test"
        >
          {showResourceTitle && <h5>{applyLocalization(resource?.title)}</h5>}

          {isEditBooking &&
            resource.id === bookingEditData?.resourceId &&
            editBookingWarning.duration &&
            !editBookingWarning.time && (
              <>
                <Alert color="danger" className="small p-2 mb-3 mt-2">
                  {t("bookingEdit.durationWarning", {
                    timeslot: bookingEditData?.timeSlot + t("min"),
                  })}
                </Alert>
              </>
            )}
          <div className="fg-unit-slot-select">
            {durations.map(item => {
              const priceDetails = getPriceByResourceSlot(
                resourcePrices,
                resource.id,
                item?.slot
              );
              return (
                <button
                  className={`btn fg-btn fg-btn-small ${resourceActiveClass(item, resource.id)} ${item?.disable || priceLoader ? "disabled" : ""}`}
                  key={item?.slot}
                  onClick={() => {
                    handleSlotClick(item, resource.id);
                    setHideDurationWarning();
                  }}
                >
                  {isFinanceConfigured &&
                    priceDetails?.priceConfigured &&
                    (priceLoader ? (
                      <div className="fg-price-skeleton d-flex gap-1 mb-1">
                        <Skeleton variant="text" width={30} height={20} />
                        <Skeleton variant="text" width={32} height={20} />
                      </div>
                    ) : (
                      <div className="mb-1">
                        {resourcePrices?.currency &&
                          " " + resourcePrices?.currency}{" "}
                        {priceDetails?.price?.toFixed(DECIMAL_PLACES)}
                      </div>
                    ))}
                  <span>
                    {item.slot}
                    {typeof item.slot === DataTypes.number && t("min")}
                  </span>
                </button>
              );
            })}
          </div>
          {durations?.length > 0 && hasWarningMessage ? (
            <div className="mt-2 mb-3">
              <div className="alert alert-warning p-2 fg-adv-booking-msg">
                {warningMessage.quote.length > 0
                  ? warningMessage.quote.map((item, index) => (
                      <div
                        key={item}
                        className={
                          index < warningMessage.quote.length - 1 ? "mb-2" : ""
                        }
                      >
                        {item}
                      </div>
                    ))
                  : warningMessage.advanceBookingMsg}
              </div>
            </div>
          ) : !isKioskMode && isResourceLocked ? (
            <Alert color="warning" className="small p-2 mt-2 mb-3">
              {t("resourceNotAvailable")}
            </Alert>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ResourceDuration;
